import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Plus, Edit2, Trash2, CheckCircle, Circle, Sparkle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = ['common_area', 'kitchen', 'bathroom', 'rooms', 'corridor', 'entrance', 'balcony', 'other'];
const FREQUENCIES = ['daily', 'weekly', 'monthly'];
const AREA_ICONS = {
  common_area: '🛋️', kitchen: '🍳', bathroom: '🚿', rooms: '🛏️',
  corridor: '🚪', entrance: '🚪', balcony: '🌿', other: '📋',
};

const CleaningChecklist = () => {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filterArea, setFilterArea] = useState('');

  const fetchProps = useCallback(async () => {
    const [pRes, tRes] = await Promise.all([
      axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/team`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setProperties(pRes.data);
    setTeamMembers(tRes.data);
    if (pRes.data.length && !propertyId) setPropertyId(pRes.data[0].id);
  }, [token, propertyId]);

  const fetchData = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [cRes, lRes, sRes] = await Promise.all([
        axios.get(`${API}/cleaning/checklists?property_id=${propertyId}${filterArea ? `&area=${filterArea}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cleaning/logs?property_id=${propertyId}&date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cleaning/summary?property_id=${propertyId}&date=${date}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setChecklists(cRes.data);
      setLogs(lRes.data);
      setSummary(sRes.data);
    } finally { setLoading(false); }
  }, [token, propertyId, date, filterArea]);

  useEffect(() => { fetchProps(); }, [fetchProps]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const isDone = (id) => logs.some(l => l.checklist_id === id && l.completed);

  const toggleComplete = async (c) => {
    const done = isDone(c.id);
    try {
      await axios.post(`${API}/cleaning/logs`, {
        checklist_id: c.id, property_id: propertyId, date, completed: !done,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/cleaning/checklists/${deleting.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Task deleted' });
      setDeleting(null); fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const grouped = checklists.reduce((acc, c) => {
    const a = c.area || 'other';
    acc[a] = acc[a] || [];
    acc[a].push(c);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cleaning-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Sparkle /> Cleaning Checklist</h1>
          <p className="text-gray-600 mt-1">Daily/weekly cleaning tasks + assignees</p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            data-testid="cleaning-property-select"
            value={propertyId}
            onChange={e => setPropertyId(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Button data-testid="add-cleaning-btn" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={16} className="mr-2" /> Add Task
          </Button>
        </div>
      </div>

      {/* Summary + date */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 border-l-4 border-l-blue-500">
            <p className="text-xs text-gray-500">Total Tasks</p>
            <p className="text-2xl font-bold mt-1">{summary.total_tasks}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-green-500">
            <p className="text-xs text-gray-500">Completed</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{summary.completed_tasks}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-orange-500">
            <p className="text-xs text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-orange-700 mt-1">{summary.pending_tasks}</p>
          </Card>
          <Card className="p-4 border-l-4 border-l-purple-500">
            <p className="text-xs text-gray-500">Completion %</p>
            <p className="text-2xl font-bold text-purple-700 mt-1">{summary.completion_pct}%</p>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <Label>Date:</Label>
        <Input data-testid="cleaning-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="w-48" />
        <select
          data-testid="cleaning-area-filter"
          value={filterArea}
          onChange={e => setFilterArea(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="">All Areas</option>
          {AREAS.map(a => <option key={a} value={a}>{AREA_ICONS[a]} {a}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={40} /></div>
      ) : checklists.length === 0 ? (
        <Card className="p-12 text-center" data-testid="cleaning-empty">
          <Sparkles size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold mb-2">No cleaning tasks yet</h3>
          <p className="text-gray-600 mb-4">Create checklist items for each area of your property</p>
          <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-2" /> Add First Task</Button>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="cleaning-list">
          {Object.entries(grouped).map(([area, items]) => (
            <Card key={area} className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3 capitalize flex items-center gap-2">
                <span>{AREA_ICONS[area]}</span> {area.replace('_', ' ')} <span className="text-xs text-gray-500">({items.length})</span>
              </h3>
              <div className="space-y-2">
                {items.map(c => {
                  const done = isDone(c.id);
                  const assignee = teamMembers.find(m => m.id === c.assigned_to);
                  return (
                    <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50" data-testid={`cleaning-item-${c.id}`}>
                      <button
                        data-testid={`cleaning-toggle-${c.id}`}
                        onClick={() => toggleComplete(c)}
                        className="hover:scale-110 transition-transform"
                      >
                        {done ? <CheckCircle className="text-green-600" size={22} /> : <Circle className="text-gray-300" size={22} />}
                      </button>
                      <div className="flex-1">
                        <p className={`font-medium ${done ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{c.task}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">{c.frequency}</span>
                          {assignee && <span>👤 {assignee.name}</span>}
                        </div>
                      </div>
                      <button data-testid={`cleaning-edit-${c.id}`} onClick={() => { setEditing(c); setShowForm(true); }} className="p-1 text-gray-500 hover:text-blue-600">
                        <Edit2 size={16} />
                      </button>
                      <button data-testid={`cleaning-delete-${c.id}`} onClick={() => setDeleting(c)} className="p-1 text-gray-500 hover:text-red-600">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <ChecklistFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          teamMembers={teamMembers}
          propertyId={propertyId}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Cleaning Task"
        message="Delete this checklist item and all its logs?"
        itemName={deleting?.task || ''}
        loading={deleteLoading}
      />
    </div>
  );
};

const ChecklistFormModal = ({ open, onClose, onSuccess, editing, teamMembers, propertyId, token }) => {
  const [form, setForm] = useState({ area: 'common_area', task: '', frequency: 'daily', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      area: editing.area || 'common_area',
      task: editing.task || '',
      frequency: editing.frequency || 'daily',
      assigned_to: editing.assigned_to || '',
    });
  }, [editing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, property_id: propertyId, assigned_to: form.assigned_to || null };
      if (editing) {
        await axios.put(`${API}/cleaning/checklists/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Task updated' });
      } else {
        await axios.post(`${API}/cleaning/checklists`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast({ title: 'Task created' });
      }
      onSuccess(); onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? 'Edit Task' : 'Add Cleaning Task'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="cleaning-form">
          <div><Label>Task *</Label><Input data-testid="cleaning-task-input" value={form.task} onChange={e => setForm({ ...form, task: e.target.value })} placeholder="e.g., Mop the kitchen floor" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Area *</Label>
              <select data-testid="cleaning-area" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                {AREAS.map(a => <option key={a} value={a}>{AREA_ICONS[a]} {a.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div>
              <Label>Frequency *</Label>
              <select data-testid="cleaning-frequency" value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Assigned To (optional)</Label>
            <select data-testid="cleaning-assignee" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">Unassigned</option>
              {teamMembers.filter(t => t.status === 'active').map(t => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="cleaning-submit" type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Add Task')}</Button>
            <Button data-testid="cleaning-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CleaningChecklist;
